import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDisplayDate, getTodayDate } from "./financialYear";

const NAVY_RGB = [26, 35, 126];
const NAVY = "#1a237e";

export const voucherLabel = (v) => v?.voucher_number || `Voucher-${v?.id}`;

const fmt = (v) => Math.round(parseFloat(v || 0)).toLocaleString("en-IN");

const fmtDate = (d) => formatDisplayDate(d, "");

const csvEscape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

const downloadBlob = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const buildFilename = (vouchers, ext) => {
    const stamp = getTodayDate();
    if (vouchers.length === 1) return `${voucherLabel(vouchers[0]).replace(/[^\w.-]+/g, "_")}.${ext}`;
    return `Vouchers_${stamp}.${ext}`;
};

const escapeHtml = (val) => String(val ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const loadImageAsDataUrl = (url) => new Promise((resolve) => {
    if (!url) {
        resolve("");
        return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        } catch (err) {
            resolve(url);
        }
    };
    img.onerror = () => resolve(url);
    img.src = url;
});

export function downloadVouchersCsv(vouchers) {
    if (!vouchers?.length) return false;

    const headers = [
        "Voucher No",
        "Voucher Date",
        "Vendor",
        "Category",
        "Item",
        "Qty",
        "Price",
        "GST %",
        "Amount",
        "Line Note",
        "Voucher Note",
    ];

    const rows = [];
    vouchers.forEach((v) => {
        const items = v.items || [];
        if (items.length === 0) {
            rows.push([
                voucherLabel(v),
                fmtDate(v.voucher_date),
                v.vendor?.name || "",
                "",
                "",
                "",
                "",
                "",
                fmt(v.total_amount),
                "",
                v.description || "",
            ]);
            return;
        }
        items.forEach((item) => {
            rows.push([
                voucherLabel(v),
                fmtDate(v.voucher_date),
                v.vendor?.name || "",
                item.head?.name || "",
                item.item_name || "",
                item.quantity ?? "",
                item.price ?? "",
                item.gst_percent ?? "",
                item.amount ?? "",
                item.description || "",
                v.description || "",
            ]);
        });
    });

    const csv = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) => row.map((cell) => csvEscape(cell)).join(",")),
    ].join("\n");

    downloadBlob(csv, buildFilename(vouchers, "csv"), "text/csv;charset=utf-8;");
    return true;
}

export async function downloadVouchersPdf(vouchers, companyName = "", options = {}) {
    if (!vouchers?.length) return false;

    const { companyLogoUrl = "", companyAddress = "" } = options;
    const logoDataUrl = companyLogoUrl ? await loadImageAsDataUrl(companyLogoUrl) : "";

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();

    vouchers.forEach((voucher, index) => {
        if (index > 0) doc.addPage();

        let y = 12;
        if (logoDataUrl) {
            try {
                doc.addImage(logoDataUrl, "PNG", pageW / 2 - 12, y, 24, 24);
                y += 28;
            } catch (err) {
                // ignore logo failures
            }
        }

        if (companyName) {
            doc.setFontSize(14);
            doc.setTextColor(...NAVY_RGB);
            doc.setFont(undefined, "bold");
            doc.text(companyName, pageW / 2, y, { align: "center" });
            y += 6;
        }
        if (companyAddress) {
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.setFont(undefined, "normal");
            const addrLines = doc.splitTextToSize(companyAddress, pageW - 40);
            doc.text(addrLines, pageW / 2, y, { align: "center" });
            y += addrLines.length * 4 + 2;
        }

        doc.setDrawColor(...NAVY_RGB);
        doc.setLineWidth(0.6);
        doc.line(14, y, pageW - 14, y);
        y += 7;

        doc.setFontSize(12);
        doc.setTextColor(...NAVY_RGB);
        doc.setFont(undefined, "bold");
        doc.text("EXPENSE VOUCHER", pageW / 2, y, { align: "center" });
        y += 8;

        doc.setFontSize(9);
        doc.setTextColor(60);
        doc.setFont(undefined, "normal");
        const info = [
            `Voucher No: ${voucherLabel(voucher)}`,
            `Date: ${fmtDate(voucher.voucher_date)}`,
            `Vendor: ${voucher.vendor?.name || "—"}`,
        ];
        if (voucher.description) info.push(`Note: ${voucher.description}`);
        info.forEach((line) => {
            doc.text(line, 14, y);
            y += 5;
        });
        y += 2;

        const items = voucher.items || [];
        const body = items.map((item, idx) => [
            idx + 1,
            item.head?.name || "—",
            item.item_name || "—",
            item.quantity ?? "",
            fmt(item.price),
            fmt(item.gst_percent),
            fmt(item.amount),
        ]);

        autoTable(doc, {
            head: [["#", "Category", "Item", "Qty", "Price", "GST %", "Amount"]],
            body,
            startY: y,
            theme: "grid",
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: NAVY_RGB, textColor: 255, fontStyle: "bold" },
            columnStyles: {
                0: { halign: "center", cellWidth: 10 },
                3: { halign: "right" },
                4: { halign: "right" },
                5: { halign: "right" },
                6: { halign: "right" },
            },
        });

        const finalY = doc.lastAutoTable.finalY + 4;
        doc.setFontSize(10);
        doc.setTextColor(...NAVY_RGB);
        doc.setFont(undefined, "bold");
        doc.text(`Voucher Total: ${fmt(voucher.total_amount)}`, pageW - 14, finalY, { align: "right" });
        doc.setFont(undefined, "normal");
    });

    doc.save(buildFilename(vouchers, "pdf"));
    return true;
}

const buildPrintHeaderHtml = (companyName, companyAddress, logoUrl) => `
    <div class="print-header">
        <div class="logo-box">
            ${logoUrl
                ? `<img src="${escapeHtml(logoUrl)}" alt="Logo" class="logo-img" />`
                : `<div class="logo-fallback">LOGO</div>`}
        </div>
        <div class="header-center">
            ${companyName ? `<div class="company-name">${escapeHtml(companyName)}</div>` : ""}
            ${companyAddress ? `<div class="company-addr">${escapeHtml(companyAddress)}</div>` : ""}
            <div class="doc-title">EXPENSE VOUCHER</div>
        </div>
        <div class="logo-spacer"></div>
    </div>
`;

export function printVouchers(vouchers, companyName = "", options = {}) {
    if (!vouchers?.length) return false;

    const { companyLogoUrl = "", companyAddress = "" } = options;
    const headerHtml = buildPrintHeaderHtml(companyName, companyAddress, companyLogoUrl);

    const html = vouchers.map((v, index) => {
        const rows = (v.items || []).map((item, idx) => `
            <tr>
                <td class="center">${idx + 1}</td>
                <td>${escapeHtml(item.head?.name || "—")}</td>
                <td><strong>${escapeHtml(item.item_name || "—")}</strong></td>
                <td class="num">${escapeHtml(item.quantity ?? "")}</td>
                <td class="num">${fmt(item.price)}</td>
                <td class="num">${fmt(item.gst_percent)}</td>
                <td class="num"><strong>${fmt(item.amount)}</strong></td>
            </tr>
        `).join("");

        return `
            <section class="voucher-block${index === vouchers.length - 1 ? " last" : ""}">
                ${headerHtml}
                <div class="voucher-meta">
                    <div><span class="meta-label">Voucher</span><span class="meta-value">${escapeHtml(voucherLabel(v))}</span></div>
                    <div><span class="meta-label">Date</span><span class="meta-value">${escapeHtml(fmtDate(v.voucher_date))}</span></div>
                    <div><span class="meta-label">Vendor</span><span class="meta-value">${escapeHtml(v.vendor?.name || "—")}</span></div>
                    ${v.description ? `<div class="meta-full"><span class="meta-label">Note</span><span class="meta-value">${escapeHtml(v.description)}</span></div>` : ""}
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style="width:36px">#</th>
                            <th>Category</th>
                            <th>Item</th>
                            <th style="width:70px">Qty</th>
                            <th style="width:90px">Price</th>
                            <th style="width:70px">GST %</th>
                            <th style="width:100px">Amount</th>
                        </tr>
                    </thead>
                    <tbody>${rows || `<tr><td colspan="7" class="center muted">No line items</td></tr>`}</tbody>
                    <tfoot>
                        <tr>
                            <td colspan="6" class="total-label">Voucher Total</td>
                            <td class="num total-value">${fmt(v.total_amount)}</td>
                        </tr>
                    </tfoot>
                </table>
                <div class="sign-row">
                    <div class="sign-box">Prepared By</div>
                    <div class="sign-box">Checked By</div>
                    <div class="sign-box">Authorized Signatory</div>
                </div>
            </section>
        `;
    }).join('<div class="page-break"></div>');

    const fullHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Expense Vouchers</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    margin: 0;
    padding: 16px;
    color: #1f2937;
    background: #fff;
  }
  .voucher-block {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .page-break {
    break-after: page;
    page-break-after: always;
    height: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }
  .print-header {
    display: flex;
    align-items: center;
    gap: 14px;
    border-bottom: 2px solid ${NAVY};
    padding-bottom: 12px;
    margin-bottom: 14px;
  }
  .logo-box, .logo-spacer {
    width: 88px;
    height: 88px;
    flex-shrink: 0;
  }
  .logo-box {
    border: 1px solid #e5e7eb;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
  }
  .logo-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .logo-fallback {
    font-size: 11px;
    color: #9ca3af;
    font-weight: 700;
    letter-spacing: 1px;
  }
  .header-center {
    flex: 1;
    text-align: center;
  }
  .company-name {
    font-size: 20px;
    font-weight: 800;
    color: ${NAVY};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 4px;
  }
  .company-addr {
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 6px;
    line-height: 1.35;
  }
  .doc-title {
    font-size: 15px;
    font-weight: 700;
    color: ${NAVY};
    letter-spacing: 1px;
  }
  .voucher-meta {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px 16px;
    background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
    border: 1px solid #c7d2fe;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 12px;
    font-size: 12px;
  }
  .meta-full { grid-column: 1 / -1; }
  .meta-label {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
    margin-bottom: 2px;
  }
  .meta-value {
    font-weight: 700;
    color: #111827;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-bottom: 18px;
  }
  th {
    background: ${NAVY};
    color: #fff !important;
    padding: 9px 8px;
    border: 1px solid ${NAVY};
    text-align: left;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 10px;
  }
  td {
    padding: 8px;
    border: 1px solid #d1d5db;
    vertical-align: top;
  }
  tbody tr:nth-child(even) td { background: #f9fafb; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .center { text-align: center; }
  .muted { color: #9ca3af; }
  tfoot td {
    background: #fff8e1;
    font-weight: 700;
  }
  .total-label { text-align: right; }
  .total-value { color: #15803d; font-size: 13px; }
  .sign-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 18px;
    margin-top: 28px;
  }
  .sign-box {
    border-top: 1px solid #9ca3af;
    padding-top: 6px;
    text-align: center;
    font-size: 11px;
    color: #4b5563;
    font-weight: 600;
  }
  @page { size: A4; margin: 12mm; }
  @media print {
    body { padding: 0; }
    .voucher-block {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .page-break {
      break-after: page;
      page-break-after: always;
    }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
  }
  @media screen {
    .voucher-block {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      margin-bottom: 20px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .page-break {
      height: 1px;
      margin: 8px 0 20px;
      background: #e5e7eb;
    }
  }
</style>
</head>
<body>
${html}
<script>
  (function () {
    function triggerPrint() {
      window.focus();
      window.print();
    }
    function whenReady(cb) {
      var imgs = Array.prototype.slice.call(document.images || []);
      if (!imgs.length) {
        setTimeout(cb, 200);
        return;
      }
      var left = imgs.length;
      var done = function () {
        left -= 1;
        if (left <= 0) setTimeout(cb, 150);
      };
      imgs.forEach(function (img) {
        if (img.complete) done();
        else {
          img.addEventListener("load", done);
          img.addEventListener("error", done);
        }
      });
      setTimeout(cb, 2500);
    }
    if (document.readyState === "complete") whenReady(triggerPrint);
    else window.addEventListener("load", function () { whenReady(triggerPrint); });
  })();
</script>
</body>
</html>`;

    const blob = new Blob([fullHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
        URL.revokeObjectURL(url);
        return false;
    }

    // Revoke after print window has loaded the blob
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
}
